import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, Paper, Chip, Divider } from '@mui/material';
import { MapContainer, TileLayer, CircleMarker, Popup, GeoJSON, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import {
  SchoolData,
  DistrictAgg,
  ChoroplethIndicator,
  NearestNeighborResult,
  DistanceFromSelected,
  ZANZIBAR_CENTER,
  DEFAULT_ZOOM,
  LEVEL_COLORS,
  LEVEL_ID_MAP,
  CHOROPLETH_COLORS,
  TEACHERS_COLORS,
  SCHOOLS_COLORS,
  PTR_COLORS,
  GPI_COLORS,
  getSchoolLevel,
} from './types';
import { computeDistancesFromSchool } from './geoUtils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MapViewProps {
  filteredSchools: SchoolData[];
  gpsSchools: SchoolData[];
  districtAggregates: Map<string, DistrictAgg>;
  districtsGeoJSON: any;
  regionsGeoJSON: any;
  choroplethIndicator: ChoroplethIndicator;
  islandFilter: string;
  regionFilter: string;
  districtFilter: string;
  typeFilters: Record<string, boolean>;
  levelFilters: Record<string, boolean>;
  coastalZoneGeoJSON?: any;
  showCoastalZone: boolean;
  nearestNeighborMap: Map<string, NearestNeighborResult>;
}

// ---------------------------------------------------------------------------
// MapController — auto-zoom to the filtered geographic area
// ---------------------------------------------------------------------------

const MapController: React.FC<{
  geoJson: any;
  districtAggregates: Map<string, DistrictAgg>;
  islandFilter: string;
  regionFilter: string;
  districtFilter: string;
}> = ({ geoJson, districtAggregates, islandFilter, regionFilter, districtFilter }) => {
  const map = useMap();

  useEffect(() => {
    if (!geoJson) return;

    // If no filter, reset to default view
    if (!islandFilter && !regionFilter && !districtFilter) {
      map.setView(ZANZIBAR_CENTER, DEFAULT_ZOOM);
      return;
    }

    // Find features that match the current filter (have data in districtAggregates)
    const matchingFeatures = geoJson.features.filter((f: any) => {
      const distName = (f.properties?.dist_name || '').toLowerCase().trim();
      return districtAggregates.has(distName);
    });

    if (matchingFeatures.length > 0) {
      const tempLayer = L.geoJSON({
        type: 'FeatureCollection',
        features: matchingFeatures,
      } as any);
      const bounds = tempLayer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 });
      }
    }
  }, [map, geoJson, districtAggregates, islandFilter, regionFilter, districtFilter]);

  return null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve the numeric indicator value for a DistrictAgg entry. */
const getIndicatorValue = (data: DistrictAgg, indicator: ChoroplethIndicator): number => {
  switch (indicator) {
    case 'enrollment':
      return data.total_students;
    case 'teachers':
      return data.total_teachers;
    case 'schools':
      return data.total_schools;
    case 'ptr':
      return data.ptr;
    case 'gpi':
      return data.gpi ?? 0;
    default:
      return 0;
  }
};

/** Human-readable label for each indicator. */
const getIndicatorLabel = (indicator: ChoroplethIndicator): string => {
  const labels: Record<ChoroplethIndicator, string> = {
    enrollment: 'Students',
    teachers: 'Teachers',
    schools: 'Schools',
    ptr: 'PTR',
    gpi: 'GPI',
  };
  return labels[indicator];
};

/** Pick the right color scale for the given indicator. */
const getColorScale = (indicator: ChoroplethIndicator): string[] => {
  const cs =
    indicator === 'teachers'
      ? TEACHERS_COLORS
      : indicator === 'schools'
        ? SCHOOLS_COLORS
        : indicator === 'ptr'
          ? PTR_COLORS
          : indicator === 'gpi'
            ? GPI_COLORS
            : CHOROPLETH_COLORS;
  return cs;
};

/** Marker radius based on enrollment size. */
const getMarkerRadius = (school: SchoolData): number => {
  if (school.current_enrollment > 1000) return 8;
  if (school.current_enrollment > 500) return 6;
  if (school.current_enrollment > 100) return 5;
  return 4;
};

/** Marker fill color based on school level. */
const getMarkerColor = (school: SchoolData): string => {
  return LEVEL_COLORS[getSchoolLevel(school)] || LEVEL_COLORS['Unknown'];
};

// ---------------------------------------------------------------------------
// MapView component
// ---------------------------------------------------------------------------

const MapView: React.FC<MapViewProps> = ({
  filteredSchools,
  gpsSchools,
  districtAggregates,
  districtsGeoJSON,
  regionsGeoJSON,
  choroplethIndicator,
  islandFilter,
  regionFilter,
  districtFilter,
  typeFilters,
  levelFilters,
  coastalZoneGeoJSON,
  showCoastalZone,
  nearestNeighborMap,
}) => {
  // -----------------------------------------------------------------------
  // Interactive distance state
  // -----------------------------------------------------------------------
  const [selectedSchool, setSelectedSchool] = useState<SchoolData | null>(null);
  const [nearbyDistances, setNearbyDistances] = useState<DistanceFromSelected[]>([]);

  // -----------------------------------------------------------------------
  // Classification breaks — equal-interval for colour spread
  // -----------------------------------------------------------------------

  const { breaks, colorScale } = useMemo(() => {
    const values: number[] = [];
    districtAggregates.forEach((d) => {
      const val = getIndicatorValue(d, choroplethIndicator);
      if (val > 0) values.push(val);
    });

    const cs = getColorScale(choroplethIndicator);

    if (values.length === 0) return { breaks: [1], colorScale: cs };

    const min = Math.min(...values);
    const max = Math.max(...values);
    const numClasses = 5;

    const b: number[] = [];
    if (min === max) {
      b.push(max);
    } else {
      const interval = (max - min) / numClasses;
      for (let i = 1; i <= numClasses; i++) {
        // For ratio indicators (ptr, gpi) keep two decimal places
        const raw = min + interval * i;
        b.push(
          choroplethIndicator === 'ptr' || choroplethIndicator === 'gpi'
            ? parseFloat(raw.toFixed(2))
            : Math.round(raw),
        );
      }
    }

    return { breaks: b, colorScale: cs };
  }, [districtAggregates, choroplethIndicator]);

  // -----------------------------------------------------------------------
  // District choropleth colour lookup
  // -----------------------------------------------------------------------

  const getDistrictColor = useCallback(
    (value: number) => {
      if (value <= 0) return '#f5f5f5';
      for (let i = 0; i < breaks.length; i++) {
        if (value <= breaks[i]) return colorScale[i] || colorScale[colorScale.length - 1];
      }
      return colorScale[colorScale.length - 1];
    },
    [breaks, colorScale],
  );

  // -----------------------------------------------------------------------
  // GeoJSON style — dim non-matching districts when filtered
  // -----------------------------------------------------------------------

  const districtStyle = useCallback(
    (feature?: GeoJSON.Feature) => {
      if (!feature)
        return { fillColor: '#f5f5f5', weight: 1, opacity: 1, color: '#666', fillOpacity: 0.6 };

      const distName = (feature.properties?.dist_name || '').toLowerCase().trim();
      const data = districtAggregates.get(distName);
      const hasData = !!data;
      const value = data ? getIndicatorValue(data, choroplethIndicator) : 0;

      const isFiltered = islandFilter || regionFilter || districtFilter;
      if (isFiltered && !hasData) {
        return {
          fillColor: '#e0e0e0',
          weight: 0.5,
          opacity: 0.3,
          color: '#999',
          fillOpacity: 0.15,
        };
      }

      return {
        fillColor: getDistrictColor(value),
        weight: hasData ? 2 : 1,
        opacity: 0.8,
        color: hasData ? '#333' : '#666',
        fillOpacity: 0.75,
      };
    },
    [districtAggregates, choroplethIndicator, getDistrictColor, islandFilter, regionFilter, districtFilter],
  );

  // -----------------------------------------------------------------------
  // Region border style (thin blue overlay)
  // -----------------------------------------------------------------------

  const regionStyle = useCallback(
    () => ({
      fillColor: 'transparent',
      weight: 3,
      opacity: 0.8,
      color: '#1565C0',
      fillOpacity: 0,
    }),
    [],
  );

  // -----------------------------------------------------------------------
  // District popup on click
  // -----------------------------------------------------------------------

  const onEachDistrict = useCallback(
    (feature: GeoJSON.Feature, layer: L.Layer) => {
      const distName = (feature.properties?.dist_name || '').toLowerCase().trim();
      const data = districtAggregates.get(distName);
      if (data) {
        const ptr =
          data.total_teachers > 0
            ? Math.round(data.total_students / data.total_teachers)
            : 'N/A';
        const gpi =
          data.male_students > 0
            ? (data.female_students / data.male_students).toFixed(2)
            : 'N/A';
        layer.bindPopup(`
          <div style="min-width:200px">
            <strong style="font-size:14px">${data.district_name}</strong>
            <div style="color:#666;font-size:11px;margin-bottom:6px">${data.region_name}</div>
            <hr style="margin:4px 0;border-color:#eee"/>
            <div style="display:flex;justify-content:space-between;padding:2px 0">
              <span>Schools:</span><strong>${data.total_schools.toLocaleString()}</strong>
            </div>
            <div style="display:flex;justify-content:space-between;padding:2px 0">
              <span>Students:</span><strong>${data.total_students.toLocaleString()}</strong>
            </div>
            <div style="display:flex;justify-content:space-between;padding:2px 0">
              <span style="color:#1976D2">Male:</span><strong>${data.male_students.toLocaleString()}</strong>
            </div>
            <div style="display:flex;justify-content:space-between;padding:2px 0">
              <span style="color:#E91E63">Female:</span><strong>${data.female_students.toLocaleString()}</strong>
            </div>
            <div style="display:flex;justify-content:space-between;padding:2px 0">
              <span>Teachers:</span><strong>${data.total_teachers.toLocaleString()}</strong>
            </div>
            <hr style="margin:4px 0;border-color:#eee"/>
            <div style="display:flex;justify-content:space-between;padding:2px 0">
              <span>PTR:</span><strong>${ptr}:1</strong>
            </div>
            <div style="display:flex;justify-content:space-between;padding:2px 0">
              <span>GPI:</span><strong>${gpi}</strong>
            </div>
          </div>
        `);
      } else {
        layer.bindPopup(
          `<strong>${feature.properties?.dist_name || 'Unknown'}</strong><br/>No data`,
        );
      }
    },
    [districtAggregates],
  );

  // -----------------------------------------------------------------------
  // GeoJSON key — force re-render when any dependency changes
  // -----------------------------------------------------------------------

  const geoJsonKey = useMemo(() => {
    const typeKey = Object.entries(typeFilters)
      .map(([k, v]) => `${k}:${v}`)
      .join(',');
    const levelKey = Object.entries(levelFilters)
      .map(([k, v]) => `${k}:${v}`)
      .join(',');
    return `${choroplethIndicator}-${islandFilter}-${regionFilter}-${districtFilter}-${typeKey}-${levelKey}`;
  }, [choroplethIndicator, islandFilter, regionFilter, districtFilter, typeFilters, levelFilters]);

  const indicatorLabel = getIndicatorLabel(choroplethIndicator);
  const isRatioIndicator = choroplethIndicator === 'ptr' || choroplethIndicator === 'gpi';

  // -----------------------------------------------------------------------
  // Format legend range labels (ratios keep decimals, counts are integers)
  // -----------------------------------------------------------------------

  const formatBreakValue = (value: number): string => {
    if (isRatioIndicator) return value.toFixed(2);
    return value.toLocaleString();
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <MapContainer
        center={ZANZIBAR_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%' }}
        zoomControl
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Auto-zoom to filtered area */}
        <MapController
          geoJson={districtsGeoJSON}
          districtAggregates={districtAggregates}
          islandFilter={islandFilter}
          regionFilter={regionFilter}
          districtFilter={districtFilter}
        />

        {/* District choropleth */}
        {districtsGeoJSON && (
          <GeoJSON
            key={geoJsonKey}
            data={districtsGeoJSON}
            style={districtStyle}
            onEachFeature={onEachDistrict}
          />
        )}

        {/* Region borders overlay */}
        {regionsGeoJSON && <GeoJSON data={regionsGeoJSON} style={regionStyle} />}

        {/* Coastal zone overlay */}
        {showCoastalZone && coastalZoneGeoJSON && (
          <GeoJSON
            key="coastal-zone"
            data={coastalZoneGeoJSON}
            style={() => ({
              fillColor: '#0288D1',
              fillOpacity: 0.15,
              color: '#0288D1',
              weight: 2,
              dashArray: '5,5',
              opacity: 0.6,
            })}
          />
        )}

        {/* Individual school markers (only those with GPS) */}
        {gpsSchools.map((school) => {
          const level = getSchoolLevel(school);
          const ptr =
            school.total_teachers > 0
              ? Math.round(school.current_enrollment / school.total_teachers)
              : null;

          return (
            <CircleMarker
              key={school.school_id}
              center={[school.latitude!, school.longitude!]}
              radius={getMarkerRadius(school)}
              pathOptions={{
                fillColor: getMarkerColor(school),
                color: '#fff',
                weight: 1.5,
                fillOpacity: 0.9,
              }}
              eventHandlers={{
                click: () => {
                  setSelectedSchool(school);
                  const distances = computeDistancesFromSchool(school, gpsSchools, 5);
                  setNearbyDistances(distances);
                },
              }}
            >
              <Popup>
                <Box sx={{ minWidth: 220 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}
                  >
                    {school.school_name}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    EMIS: {school.school_reg_number}
                  </Typography>
                  <Divider sx={{ my: 0.5 }} />

                  {/* Level + Type chips */}
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={level}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        bgcolor: LEVEL_COLORS[level] || LEVEL_COLORS['Unknown'],
                        color: '#fff',
                      }}
                    />
                    <Chip
                      label={school.school_type === 'PRIVATE' ? 'Private' : 'Public'}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  </Box>

                  {/* School details */}
                  <Box sx={{ fontSize: '0.75rem' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <span>Students:</span>
                      <strong>{school.current_enrollment.toLocaleString()}</strong>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <span style={{ color: '#1976D2' }}>Male:</span>
                      <strong>{school.male_students.toLocaleString()}</strong>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <span style={{ color: '#E91E63' }}>Female:</span>
                      <strong>{school.female_students.toLocaleString()}</strong>
                    </Box>
                    <Divider sx={{ my: 0.3 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <span>Teachers:</span>
                      <strong>{school.total_teachers.toLocaleString()}</strong>
                    </Box>
                    {ptr !== null && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                        <span>PTR:</span>
                        <strong>{ptr}:1</strong>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 0.3 }} />
                  <Typography variant="caption" color="text.secondary">
                    {school.district?.district_name} - {school.region?.region_name}
                  </Typography>
                </Box>
              </Popup>
            </CircleMarker>
          );
        })}

        {/* Distance lines from selected school */}
        {selectedSchool && selectedSchool.latitude && selectedSchool.longitude && nearbyDistances.map((nd) => (
          <React.Fragment key={`dist-${nd.schoolId}`}>
            <Polyline
              positions={[
                [selectedSchool.latitude!, selectedSchool.longitude!],
                [nd.latitude, nd.longitude],
              ]}
              pathOptions={{
                color: '#D32F2F',
                weight: 2,
                dashArray: '6,6',
                opacity: 0.8,
              }}
            />
          </React.Fragment>
        ))}
      </MapContainer>

      {/* ----------------------------------------------------------------- */}
      {/* Legend overlay (bottom-right)                                      */}
      {/* ----------------------------------------------------------------- */}
      <Paper
        sx={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          zIndex: 1000,
          p: 1.5,
          opacity: 0.95,
          maxWidth: 200,
        }}
      >
        {/* Choropleth colour scale */}
        <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
          {indicatorLabel} by District
        </Typography>
        {breaks.map((b, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
            <Box
              sx={{
                width: 14,
                height: 12,
                bgcolor: colorScale[i],
                border: '1px solid #ccc',
              }}
            />
            <Typography variant="caption">
              {i === 0
                ? `0 - ${formatBreakValue(b)}`
                : `${formatBreakValue(breaks[i - 1])} - ${formatBreakValue(b)}`}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ my: 0.5 }} />

        {/* School level marker legend */}
        <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.3 }}>
          School Level
        </Typography>
        {Object.entries(LEVEL_COLORS)
          .filter(([k]) => k !== 'Unknown')
          .map(([level, color]) => (
            <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: color,
                  border: '1px solid #fff',
                  boxShadow: '0 0 2px rgba(0,0,0,0.3)',
                }}
              />
              <Typography variant="caption">{level}</Typography>
            </Box>
          ))}

        {showCoastalZone && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.3 }}>
              Coastal Zone
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
              <Box sx={{ width: 14, height: 12, bgcolor: 'rgba(2,136,209,0.3)', border: '1px dashed #0288D1' }} />
              <Typography variant="caption">Within 5km of coast</Typography>
            </Box>
          </>
        )}

        <Divider sx={{ my: 0.5 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Click a school to measure distances
        </Typography>
      </Paper>

      {/* ----------------------------------------------------------------- */}
      {/* Distance panel overlay (top-right)                                */}
      {/* ----------------------------------------------------------------- */}
      {selectedSchool && nearbyDistances.length > 0 && (
        <Paper sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 1000,
          p: 1.5,
          opacity: 0.95,
          maxWidth: 280,
          maxHeight: 300,
          overflow: 'auto',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Distances from {selectedSchool.school_name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ cursor: 'pointer', color: 'error.main', fontWeight: 600 }}
              onClick={() => { setSelectedSchool(null); setNearbyDistances([]); }}
            >
              ✕
            </Typography>
          </Box>
          {nearbyDistances.map((nd, idx) => (
            <Box key={nd.schoolId} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3, borderBottom: '1px solid #eee' }}>
              <Typography variant="caption" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {idx + 1}. {nd.schoolName}
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ color: nd.distanceKm < 1 ? '#388E3C' : nd.distanceKm > 5 ? '#D32F2F' : '#FF9800' }}>
                {nd.distanceKm.toFixed(1)} km
              </Typography>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default MapView;
